import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ConfigProvider, Table } from 'antd';
import type { TableColumnsType as ColumnsType } from 'antd';
import { EMPTY_TASK_REMOVED_MESSAGE } from 'constants/messageConstants';
import {
  OPERATION_STEP_GROUP_KEY,
  PRODUCTION_CROP_RESOLVING_TOOLTIP,
  PRODUCTION_STEP_GROUP_KEY,
  TASKS_GRID_COLUMN_LABEL_AREA,
  TASKS_GRID_COLUMN_LABEL_AREA_UNIT,
  TASKS_GRID_COLUMN_LABEL_COST_PER_HA,
  TASKS_GRID_COLUMN_LABEL_DATE,
  TASKS_GRID_COLUMN_LABEL_TASK,
  TASKS_GRID_COLUMN_LABEL_TIMING,
  TASKS_GRID_EMPTY_STATE_HINT_BUTTON_LABEL,
  TASKS_GRID_EMPTY_STATE_HINT_PREFIX,
  TASKS_GRID_EMPTY_STATE_HINT_SUFFIX,
  TASKS_GRID_EMPTY_STATE_TITLE,
  TASKS_GRID_STEP_TYPE_NOT_ALLOWED_MESSAGE,
  TASKS_SHEET_ADD_INPUT_LABEL,
} from 'constants/uiConstants';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import { IInput, IInputZone } from 'interfaces/IInput';
import { IInputStep, InputStepType } from 'interfaces/IInputStep';
import {
  IInputTask,
  IInputTaskBase,
  InputTaskCategory,
  InputTaskType,
} from 'interfaces/IInputTask';
import { PartialUserProfile } from 'interfaces/IUserProfile';
import MergeClassNames from 'utils/MergeClassNames';
import { toast } from 'utils/toast';

import AddInputControl from './AddInputControl';
import InputStepDetailsModal from './InputStepDetailsModal';
import InputTableFooter from './InputTableFooter';
import { isOtherCostTask } from './otherCosts';
import ProductionTaskSteps from './ProductionTaskSteps';
import QuickAddTaskRow, { QUICK_ADD_TASK_OPTIONS } from './QuickAddTaskRow';
import {
  buildSeedLinkedReason,
  getAreaUnitOptions,
  getLinkedSeedDressItems,
  getTaskSteps,
  isApplicationLikeTask,
  taskHasAnyApplicationDetail,
  isLiquidPlantProtectionStep,
} from './stepCalculations';
import {
  ADD_INPUT_CATEGORY_KEY_BY_FILTER,
  ALL_STEP_CATEGORY_KEY,
  getAddCategoryLabel,
  getStepCategoryFilter,
  taskMatchesStepCategory,
} from './stepCategoryFilters';
import { isStepAllowedForTask } from './stepConfig';
import {
  buildStepFromSelection,
  keepProductionStepValues,
  ProductionCropContext,
  StepSelection,
} from './stepFactory';
import TaskCategorySheet from './TaskCategorySheet';
import TaskDetailsSection from './TaskDetailsSection';
import TaskFilterTally from './TaskFilterTally';
import TaskInlineDetails from './TaskInlineDetails';
import {
  focusSheetStepValue,
  focusTaskAddInput,
  focusTaskDateCell,
  focusTaskEntryAddInputs,
  scrollTaskRowIntoView,
  TASK_ADD_INPUT_ATTR,
} from './taskEntryFocus';
import {
  ApplicationTaskFields,
  buildTask,
  compareTasksByEndDate,
  duplicateTask,
  TaskKind,
} from './taskFactory';
import { withPlanCropSeed } from './planCropDefaults';
import TaskRowContextMenu from './TaskRowContextMenu';
import {
  TASK_ROW_COLUMN_COUNT,
  TASK_ROW_COLUMN_WIDTHS_REM,
} from './taskRowLayout';
import {
  DEFAULT_TASKS_VIEW_PREFERENCES,
  TaskColumnWidth,
  TasksViewPreferences,
} from './tasksViewPreferences';
import TaskStepsTable from './TaskStepsTable';
import TaskSummaryRow from './TaskSummaryRow';
import { useStepContextMenu } from './useStepContextMenu';
import { useStepReorder } from './useStepReorder';
import { useOrgDefaultWaterRate } from './useOrgDefaultWaterRate';
import { useTaskDetailsVisibility } from './useTaskDetailsVisibility';
import {
  canAddStepTypeToTask,
  getProductionStep,
  PRODUCTION_COST_STEP_TYPES,
  syncYieldLinkedSteps,
} from './yieldLinkedCosts';

// What counts as "still working on the new task": its row and expanded steps,
// plus the pickers, menus and modals it opens (portalled outside the table).
const ENTRY_TASK_SURFACES_SELECTOR = [
  '.task-entry-row',
  '.task-entry-expanded-row',
  '.ant-popover',
  '.ant-picker-dropdown',
  '.ant-select-dropdown',
  '.ant-dropdown',
  '.ant-modal-root',
  '.row-actions-menu',
].join(', ');

const TASK_COLUMN_NOTICE_DURATION_MS = 5000;
// How long a finished or duplicated task keeps its highlight class; matches
// the 2.5s task-row-finished-fade animation in App.css.
const TASK_FINISHED_HIGHLIGHT_MS = 2500;

/** What a new task starts with that the user didn't enter. */
interface NewTaskDefaults {
  /** The plan's seed a crop establishment task starts with (OP-2536). */
  step: IInputStep | null;
}

// Nothing the user would want kept: no date, products, timing, notes or
// application details. Area isn't counted — it's pre-filled from the plan —
// and nor is the plan's seed the task started with, while it's untouched.
const isNewTaskEmpty = (
  task: IInputTask,
  gUnits: GUnit[],
  defaults: NewTaskDefaults
): boolean =>
  !task.endDate &&
  getTaskSteps(task).filter((step) => step !== defaults.step).length === 0 &&
  !task.timing &&
  !taskHasAnyApplicationDetail(task, gUnits);

interface TasksTableGridProps {
  selectedInput: IInput;
  draftTasks: IInputTask[];
  draftZone?: IInputZone | null;
  gUnits: GUnit[];
  productionCropContext: ProductionCropContext;
  userProfile?: PartialUserProfile;
  activeStepCategoryKey: string;
  activeTaskCategory: InputTaskCategory;
  /**
   * View options (OP-2556). With the new layout on, the grid is the category
   * sheet, for All and every filter. Unset or off: the original layout.
   */
  viewPreferences?: TasksViewPreferences;
  /** New layout: tasks whose products are folded to one line. */
  collapsedSheetTaskIds?: string[];
  onToggleSheetTaskCollapsed?: (taskId: string) => void;
  onTaskColumnWidthChange?: (width: TaskColumnWidth) => void;
  /** At the right end of the quick-add row (the default water rate). */
  quickAddTrailing?: React.ReactNode;
  /** The width (px) the new layout has, measured outside its own cap. */
  sheetAvailableWidth?: number;
  /** The new layout's width cap, for the toolbar above to match. */
  onSheetMaxWidthClassChange?: (maxWidthClass: string) => void;
  /** Switches the grid to the Production filter. */
  onShowProductionFilter?: () => void;
  expandedTaskKeys: string[];
  setExpandedTaskKeys: React.Dispatch<React.SetStateAction<string[]>>;
  addDraftTask: (task: IInputTask) => void;
  updateDraftTask: (task: IInputTask) => void;
  removeDraftTask: (taskId: string) => void;
  schedulePendingTaskSave: (task: IInputTask) => void;
  schedulePendingNewTaskSave: (task: IInputTask) => void;
  schedulePendingTaskDelete: (taskId: string) => void;
}

const TasksTableGrid: React.FC<TasksTableGridProps> = ({
  selectedInput,
  draftTasks,
  draftZone,
  gUnits,
  productionCropContext,
  userProfile,
  activeStepCategoryKey,
  activeTaskCategory,
  viewPreferences = DEFAULT_TASKS_VIEW_PREFERENCES,
  collapsedSheetTaskIds = [],
  onToggleSheetTaskCollapsed,
  onTaskColumnWidthChange,
  quickAddTrailing,
  onSheetMaxWidthClassChange,
  onShowProductionFilter,
  sheetAvailableWidth = 0,
  expandedTaskKeys,
  setExpandedTaskKeys,
  addDraftTask,
  updateDraftTask,
  removeDraftTask,
  schedulePendingTaskSave,
  schedulePendingNewTaskSave,
  schedulePendingTaskDelete,
}) => {
  // The task being entered from the quick-add row (OP-2534). It stays pinned
  // to the top, under the quick-add row, until it's finished — then it moves
  // into date order and is briefly highlighted.
  const [entryTaskId, setEntryTaskId] = useState<string | null>(null);
  // When an empty new task was last removed; drives the 5-second note beside
  // the Task column label. A timestamp, so a second removal restarts the timer.
  const [emptyTaskRemovedAt, setEmptyTaskRemovedAt] = useState<
    number | null
  >(null);
  const [justFinishedTaskId, setJustFinishedTaskId] = useState<string | null>(
    null
  );
  // Focus to move once the row it targets has rendered.
  const [pendingFocus, setPendingFocus] = useState<{
    taskId: string;
    target: 'date' | 'addInput' | 'row' | 'stepValue';
    /** For 'stepValue': the product just picked. */
    stepId?: string;
  } | null>(null);
  const [deferredProductionPick, setDeferredProductionPick] = useState<{
    taskId: string;
    selections: StepSelection[];
  } | null>(null);
  // The category sheet's open Task details popover (OP-2556).
  const [openDetailsTaskId, setOpenDetailsTaskId] = useState<string | null>(
    null
  );

  const isFiltered = activeStepCategoryKey !== ALL_STEP_CATEGORY_KEY;
  // The new layout's category sheet replaces the task table.
  const showSheet = viewPreferences.sheetLayout;
  const orgDefaultWaterRate = useOrgDefaultWaterRate();
  // With a filter on, tasks holding a matching product (OP-2534 AC 14).
  const matchingTaskIds = useMemo(
    () =>
      new Set(
        isFiltered
          ? draftTasks
              .filter((task) =>
                taskMatchesStepCategory(
                  getTaskSteps(task),
                  activeStepCategoryKey
                )
              )
              .map((task) => task.taskId)
          : []
      ),
    [draftTasks, activeStepCategoryKey, isFiltered]
  );

  const tasks = useMemo(() => {
    const sorted = draftTasks
      .filter((task) => task.category === activeTaskCategory)
      .sort(compareTasksByEndDate);
    // With a filter on, matching tasks come first, each group in date order.
    const ordered = isFiltered
      ? [
          ...sorted.filter((task) => matchingTaskIds.has(task.taskId)),
          ...sorted.filter((task) => !matchingTaskIds.has(task.taskId)),
        ]
      : sorted;
    const entryTask = ordered.find((task) => task.taskId === entryTaskId);
    return entryTask
      ? [entryTask, ...ordered.filter((task) => task !== entryTask)]
      : ordered;
  }, [
    draftTasks,
    activeTaskCategory,
    entryTaskId,
    isFiltered,
    matchingTaskIds,
  ]);

  // A filter is on and the task has nothing it matches: shown as a slim row,
  // never expanded (AC 15). The task being entered is never compact.
  const isCompactTask = useCallback(
    (taskId: string) =>
      isFiltered && !matchingTaskIds.has(taskId) && taskId !== entryTaskId,
    [isFiltered, matchingTaskIds, entryTaskId]
  );

  const activeFilter = getStepCategoryFilter(activeStepCategoryKey);

  // The category sheet's two parts (OP-2556): the plan — tasks with a product
  // in the category, plus the task being entered — and, below it, the tasks
  // the category could go in that have none yet. Tasks it can't go in at all
  // aren't shown. Both keep the date order of `tasks`.
  const { planTasks, missingTasks } = useMemo(() => {
    if (!showSheet) return { planTasks: [], missingTasks: [] };
    if (!isFiltered) return { planTasks: tasks, missingTasks: [] };
    const stepTypes = activeFilter?.stepTypes ?? [];
    return {
      planTasks: tasks.filter(
        (task) =>
          matchingTaskIds.has(task.taskId) || task.taskId === entryTaskId
      ),
      missingTasks: tasks.filter(
        (task) =>
          !matchingTaskIds.has(task.taskId) &&
          task.taskId !== entryTaskId &&
          stepTypes.some((stepType) =>
            canAddStepTypeToTask(stepType, task, isStepAllowedForTask)
          )
      ),
    };
  }, [
    showSheet,
    isFiltered,
    activeFilter,
    tasks,
    matchingTaskIds,
    entryTaskId,
  ]);

  // The plan with its unsaved area/unit edits (AC 5), for new tasks and the
  // nutrient captions alike.
  const planInput = useMemo(
    () => ({ ...selectedInput, zone: draftZone ?? selectedInput.zone }),
    [selectedInput, draftZone]
  );
  const [activeSelectorTaskId, setActiveSelectorTaskId] = useState<
    string | null
  >(null);
  const [editingStepInputId, setEditingStepInputId] = useState<string | null>(
    null
  );

  const closeInputSelectors = useCallback(() => {
    setActiveSelectorTaskId(null);
    setEditingStepInputId(null);
  }, []);

  // Breaks the circular dependency between the two hooks below: useStepReorder
  // needs to close the context menu on drag-start, and useStepContextMenu needs
  // to start a reorder from its "Reorder" menu item. The ref is assigned to the
  // real function below, synchronously during render and before either hook's
  // handlers can be invoked by a user interaction.
  const closeContextMenuRef = useRef<() => void>(() => {});
  const closeContextMenu = useCallback(() => {
    closeContextMenuRef.current();
  }, []);

  const stepReorder = useStepReorder({
    tasks,
    activeStepCategoryKey,
    activeTaskCategory,
    updateDraftTask,
    schedulePendingTaskSave,
    closeContextMenu,
    closeInputSelectors,
  });
  const { startReorder, reset: resetReorder } = stepReorder;

  const stepContextMenu = useStepContextMenu({
    tasks,
    activeStepCategoryKey,
    removeDraftTask,
    schedulePendingTaskDelete,
    updateDraftTask,
    schedulePendingTaskSave,
    closeInputSelectors,
    onReorder: startReorder,
  });
  closeContextMenuRef.current = stepContextMenu.closeContextMenu;
  const {
    contextMenuState,
    contextMenuRef,
    viewDetailsStep,
    closeViewDetailsStep,
    showViewDetails,
    canReorder: contextMenuCanReorder,
    deleteDisabledReason,
    handleTaskRowContextMenu,
    openMenuFromKebab,
    handleViewDetailsFromContext,
    handleDeleteFromContext,
    handleReorderFromContext,
    reset: resetContextMenu,
  } = stepContextMenu;

  const {
    fieldVisibilityOverride,
    setFieldVisible,
    reset: resetDetailsVisibility,
  } = useTaskDetailsVisibility();

  const previousInputIdRef = useRef(selectedInput.id);

  if (selectedInput.id !== previousInputIdRef.current) {
    previousInputIdRef.current = selectedInput.id;
    setActiveSelectorTaskId(null);
    setEditingStepInputId(null);
    resetContextMenu();
    resetReorder();
    resetDetailsVisibility();
    setEntryTaskId(null);
    setJustFinishedTaskId(null);
    setPendingFocus(null);
    setEmptyTaskRemovedAt(null);
    setOpenDetailsTaskId(null);
  }

  const areaUnitOptions = useMemo(() => getAreaUnitOptions(gUnits), [gUnits]);

  const commitTaskField = useCallback(
    (
      task: IInputTask,
      patch: Partial<IInputTaskBase & ApplicationTaskFields>
    ) => {
      const updatedTask = { ...task, ...patch } as IInputTask;
      if ('area' in patch || 'areaUnit' in patch) {
        updatedTask.steps = updatedTask.steps.map((step) => ({
          ...step,
          area: updatedTask.area,
          areaUnit: updatedTask.areaUnit,
        }));
      }
      updateDraftTask(updatedTask);
      schedulePendingTaskSave(updatedTask);
    },
    [updateDraftTask, schedulePendingTaskSave]
  );

  const commitStepField = useCallback(
    (task: IInputTask, stepId: string, patch: Partial<IInputStep>) => {
      // A yield change carries to the task's yield-linked costs (OP-2399).
      const updatedTask: IInputTask = syncYieldLinkedSteps({
        ...task,
        steps: task.steps.map((step) =>
          step.stepId === stepId ? ({ ...step, ...patch } as IInputStep) : step
        ),
      });
      updateDraftTask(updatedTask);
      schedulePendingTaskSave(updatedTask);
    },
    [updateDraftTask, schedulePendingTaskSave]
  );

  // What the task being entered started with, so finishing it untouched
  // still cancels it.
  const entryDefaultsRef = useRef<NewTaskDefaults>({ step: null });

  const finishEntry = useCallback(() => {
    if (!entryTaskId) return;
    setEntryTaskId(null);
    setActiveSelectorTaskId(null);

    // A new task left with nothing entered was most likely an accidental
    // add, so finishing it cancels it rather than dropping a blank task at
    // the bottom of the list. Within the save debounce this never reaches the
    // server; after it, the delete removes the empty record.
    const entryTask = draftTasks.find((task) => task.taskId === entryTaskId);
    if (
      entryTask &&
      isNewTaskEmpty(entryTask, gUnits, entryDefaultsRef.current)
    ) {
      removeDraftTask(entryTask.taskId);
      schedulePendingTaskDelete(entryTask.taskId);
      setExpandedTaskKeys((previous) =>
        previous.filter((key) => key !== entryTask.taskId)
      );
      setEmptyTaskRemovedAt(Date.now());
      return;
    }

    setJustFinishedTaskId(entryTaskId);
    setPendingFocus({ taskId: entryTaskId, target: 'row' });
  }, [
    entryTaskId,
    draftTasks,
    gUnits,
    removeDraftTask,
    schedulePendingTaskDelete,
    setExpandedTaskKeys,
  ]);

  const handleAddTask = useCallback(
    (kind: TaskKind) => {
      if (!selectedInput) return;
      setEmptyTaskRemovedAt(null);
      // Adding the next task finishes the one being entered, so Alt/⌥+A/C/P
      // is also "finish and start the next one".
      finishEntry();
      // planInput carries area/unit edits not yet saved (AC 5), so a new
      // task matches what the plan header shows right now.
      // A crop establishment task starts with the plan's crop and variety
      // (OP-2536), including a crop change not saved yet.
      const task = withPlanCropSeed(
        buildTask(planInput, kind, activeTaskCategory, userProfile),
        productionCropContext.crop,
        productionCropContext.cropOption,
        userProfile
      );
      entryDefaultsRef.current = { step: task.steps[0] ?? null };
      addDraftTask(task);
      schedulePendingNewTaskSave(task);
      // Expanded so its "+ Add" product button is there to move to once the
      // date is set. The Application Details card is left closed: most
      // agronomists set the date and products first.
      setExpandedTaskKeys((previous) =>
        previous.includes(task.taskId) ? previous : [...previous, task.taskId]
      );
      setEntryTaskId(task.taskId);
      // In the category sheet the date is in the Task details popover.
      if (showSheet) setOpenDetailsTaskId(task.taskId);
      setPendingFocus({ taskId: task.taskId, target: 'date' });
    },
    [
      addDraftTask,
      schedulePendingNewTaskSave,
      selectedInput,
      planInput,
      activeTaskCategory,
      productionCropContext.crop,
      productionCropContext.cropOption,
      userProfile,
      setExpandedTaskKeys,
      finishEntry,
      showSheet,
    ]
  );

  useEffect(() => {
    if (!pendingFocus) return;
    // Wait a frame so the new or re-sorted row is in the DOM.
    const frame = requestAnimationFrame(() => {
      scrollTaskRowIntoView(pendingFocus.taskId);
      if (pendingFocus.target === 'date') {
        focusTaskDateCell(pendingFocus.taskId);
      } else if (pendingFocus.target === 'addInput') {
        focusTaskAddInput(pendingFocus.taskId);
      } else if (pendingFocus.target === 'stepValue' && pendingFocus.stepId) {
        focusSheetStepValue(pendingFocus.taskId, pendingFocus.stepId);
      }
      setPendingFocus(null);
    });
    return () => cancelAnimationFrame(frame);
  }, [pendingFocus]);

  useEffect(() => {
    if (emptyTaskRemovedAt === null) return;
    const timeout = window.setTimeout(
      () => setEmptyTaskRemovedAt(null),
      TASK_COLUMN_NOTICE_DURATION_MS
    );
    return () => window.clearTimeout(timeout);
  }, [emptyTaskRemovedAt]);

  useEffect(() => {
    if (!justFinishedTaskId) return;
    const timeout = window.setTimeout(
      () => setJustFinishedTaskId(null),
      TASK_FINISHED_HIGHLIGHT_MS
    );
    return () => window.clearTimeout(timeout);
  }, [justFinishedTaskId]);

  // Once the new task's date picker closes, go straight to adding products —
  // but only while it has none, so re-editing the date later doesn't reopen
  // the picker. Deferred a tick so the click that closed the date picker
  // can't land as an outside click on the product picker and close it.
  const handleDateEditEnd = useCallback(
    (taskId: string) => {
      if (taskId !== entryTaskId) return;
      const task = tasks.find((candidate) => candidate.taskId === taskId);
      if (!task || getTaskSteps(task).length > 0) return;
      // New layout: stay in the task's popover, with focus on "Add inputs",
      // so Enter moves on and Tab reaches the other fields. Nothing else
      // advances on its own, as most fields may stay empty (OP-2556). A fixed
      // cost has no other fields to fill, so it goes on to the picker
      // (OP-2399).
      if (showSheet && !isOtherCostTask(task)) {
        requestAnimationFrame(() => focusTaskEntryAddInputs(taskId));
        return;
      }
      setOpenDetailsTaskId(null);
      window.setTimeout(() => setActiveSelectorTaskId(taskId), 0);
    },
    [entryTaskId, tasks, showSheet]
  );

  // The new task popover's "Add inputs": close it and open the product
  // picker, deferred a tick so the click can't land as an outside click on
  // the picker and close it.
  const handleEntryAddInputs = useCallback((taskId: string) => {
    setOpenDetailsTaskId(null);
    window.setTimeout(() => setActiveSelectorTaskId(taskId), 0);
  }, []);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      // Escape on the new task's "+ Add" button (where focus lands after each
      // product) finishes it: Escape closes the product picker, Escape again
      // finishes the task.
      if (event.key === 'Escape' && entryTaskId && !activeSelectorTaskId) {
        const addInputOwner = (event.target as HTMLElement | null)
          ?.closest?.(`[${TASK_ADD_INPUT_ATTR}]`)
          ?.getAttribute(TASK_ADD_INPUT_ATTR);
        if (addInputOwner === entryTaskId) {
          event.preventDefault();
          finishEntry();
        }
        return;
      }

      const target = event.target as HTMLElement | null;
      const isTextField =
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLInputElement ||
        !!target?.isContentEditable;

      // Alt/⌥ + A, C, P or O adds a task (OP-2556, OP-2399). Not while
      // typing in a field, where ⌥ + a letter types a character on a Mac.
      if (event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
        const quickAddOption = QUICK_ADD_TASK_OPTIONS.find(
          (option) => option.shortcutCode === event.code
        );
        if (quickAddOption && !isTextField) {
          event.preventDefault();
          handleAddTask(quickAddOption.kind);
        }
        return;
      }

      if (!(event.ctrlKey || event.metaKey)) return;

      // Ctrl/⌘+Enter also finishes the task being entered. Skipped while the
      // product picker is open (it has its own Ctrl/⌘+Enter) and inside text
      // fields, which may use it themselves.
      if (event.key === 'Enter') {
        if (
          entryTaskId &&
          !activeSelectorTaskId &&
          !event.defaultPrevented &&
          !isTextField
        ) {
          event.preventDefault();
          finishEntry();
        }
        return;
      }
    };

    document.addEventListener('keydown', handleShortcut);
    return () => document.removeEventListener('keydown', handleShortcut);
  }, [handleAddTask, finishEntry, entryTaskId, activeSelectorTaskId]);

  // Clicking anywhere outside the new task — its row, its steps, or a picker
  // or menu it opened — finishes it, so there's no Done step to remember.
  useEffect(() => {
    if (!entryTaskId) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (!target?.closest || target.closest(ENTRY_TASK_SURFACES_SELECTOR)) {
        return;
      }
      finishEntry();
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [entryTaskId, finishEntry]);

  // Duplicate (AC 7): the copy shares the original's date, so it sorts beside
  // it; it opens expanded and is briefly highlighted like a finished new task.
  const handleDuplicateFromContext = useCallback(() => {
    const target = contextMenuState?.target;
    closeContextMenu();
    if (!target || target.rowType !== 'task') return;
    const source = tasks.find((task) => task.taskId === target.taskId);
    if (!source) return;
    finishEntry();
    const copy = duplicateTask(source, userProfile);
    addDraftTask(copy);
    schedulePendingNewTaskSave(copy);
    setExpandedTaskKeys((previous) => [...previous, copy.taskId]);
    setJustFinishedTaskId(copy.taskId);
    setPendingFocus({ taskId: copy.taskId, target: 'row' });
  }, [
    contextMenuState,
    closeContextMenu,
    tasks,
    finishEntry,
    userProfile,
    addDraftTask,
    schedulePendingNewTaskSave,
    setExpandedTaskKeys,
  ]);

  const toggleTaskExpand = useCallback(
    (taskId: string) => {
      // Collapsing the task being entered ends its entry.
      if (taskId === entryTaskId) finishEntry();
      // Collapsing unmounts the steps wrapper, and React does not fire onBlur
      // on unmount — without this the grid would stay in reorder mode with
      // nothing focused and the arrow keys dead.
      resetReorder();
      setExpandedTaskKeys((previous) =>
        previous.includes(taskId)
          ? previous.filter((key) => key !== taskId)
          : [...previous, taskId]
      );
    },
    [resetReorder, setExpandedTaskKeys, entryTaskId, finishEntry]
  );

  const closeInputSelector = useCallback(() => {
    // While a task is being entered, closing its picker hands focus back to
    // its "+ Add" button: Enter adds another product, Ctrl/⌘+Enter finishes.
    if (activeSelectorTaskId && activeSelectorTaskId === entryTaskId) {
      setPendingFocus({ taskId: activeSelectorTaskId, target: 'addInput' });
    }
    setActiveSelectorTaskId(null);
  }, [activeSelectorTaskId, entryTaskId]);

  // The latest tasks, so a pick adds to the task as it is now — a date set
  // just before the picker opened included — without re-creating every
  // row's handler on each edit.
  const draftTasksRef = useRef(draftTasks);
  draftTasksRef.current = draftTasks;

  const handleStepSelect = useCallback(
    (pickedTask: IInputTask, selections: StepSelection[]) => {
      if (selections.length === 0) return;
      const task =
        draftTasksRef.current.find(
          (candidate) => candidate.taskId === pickedTask.taskId
        ) ?? pickedTask;

      const disallowed = selections.find(
        (selection) => !isStepAllowedForTask(selection.stepType, task.taskType)
      );

      if (disallowed) {
        toast.error(TASKS_GRID_STEP_TYPE_NOT_ALLOWED_MESSAGE);
        return;
      }

      const isProductionTask = task.taskType === InputTaskType.Production;
      // A production task holds one production step, seeded from the
      // resolved crop, and its yield-linked Operation costs, picked together
      // or in any order (OP-2399).
      const isProductionPick =
        isProductionTask &&
        selections.some(
          (selection) => selection.stepType === InputStepType.Production
        );
      // The crop or the tonne unit is still loading: the pick is kept and
      // added once they're in, rather than dropped with the picker closed.
      if (isProductionPick && productionCropContext.isResolving) {
        setDeferredProductionPick({ taskId: task.taskId, selections });
        setActiveSelectorTaskId(null);
        return;
      }
      const productionSelection = isProductionPick
        ? selections.find(
            (selection) => selection.stepType === InputStepType.Production
          )
        : undefined;
      const allowedSelections = isProductionTask
        ? [
            ...(productionSelection &&
            canAddStepTypeToTask(
              InputStepType.Production,
              task,
              isStepAllowedForTask
            )
              ? [productionSelection]
              : []),
            ...selections.filter(
              (selection) => selection.stepType !== InputStepType.Production
            ),
          ]
        : selections;
      if (allowedSelections.length === 0) {
        setActiveSelectorTaskId(null);
        return;
      }

      const newSteps = allowedSelections.map((selection) =>
        buildStepFromSelection(
          task,
          selection,
          userProfile,
          productionCropContext
        )
      );

      const withSteps: IInputTask = syncYieldLinkedSteps({
        ...task,
        steps: [...getTaskSteps(task), ...newSteps],
      });
      // New layout (OP-2556): adding a liquid plant protection product to a
      // task with no water rate fills in the organisation's default, so it
      // isn't entered over and over. Nothing else does. The Task cell marks
      // it "default".
      const updatedTask: IInputTask =
        showSheet &&
        orgDefaultWaterRate !== null &&
        isApplicationLikeTask(withSteps) &&
        withSteps.waterRate === null &&
        newSteps.some((step) => isLiquidPlantProtectionStep(step, gUnits))
          ? { ...withSteps, waterRate: orgDefaultWaterRate }
          : withSteps;

      updateDraftTask(updatedTask);
      schedulePendingTaskSave(updatedTask);
      setActiveSelectorTaskId(null);
      // As for other tasks, focus goes back to "+ Add" so more can follow —
      // a Production task's yield-linked costs too (OP-2399).
      if (task.taskId === entryTaskId && !showSheet) {
        setPendingFocus({ taskId: task.taskId, target: 'addInput' });
      }
      // New layout: straight on to the product's rate (a production step's
      // lowest yield), the number entered next.
      if (showSheet && newSteps[0]) {
        setPendingFocus({
          taskId: task.taskId,
          target: 'stepValue',
          stepId: newSteps[0].stepId,
        });
      }
    },
    [
      updateDraftTask,
      schedulePendingTaskSave,
      userProfile,
      productionCropContext,
      entryTaskId,
      showSheet,
      orgDefaultWaterRate,
      gUnits,
    ]
  );

  // A production pick made while the crop was loading, added once it's in.
  useEffect(() => {
    if (!deferredProductionPick || productionCropContext.isResolving) return;
    setDeferredProductionPick(null);
    const task = draftTasks.find(
      (candidate) => candidate.taskId === deferredProductionPick.taskId
    );
    if (task) handleStepSelect(task, deferredProductionPick.selections);
  }, [
    deferredProductionPick,
    draftTasks,
    handleStepSelect,
    productionCropContext.isResolving,
  ]);

  // New layout: the water rate to offer a task that needs one and has none —
  // the organisation's default, else the last earlier application's.
  const waterRateSuggestions = useMemo(() => {
    const suggestions = new Map<string, number>();
    if (!showSheet) return suggestions;
    let previous: number | null = null;
    [...draftTasks].sort(compareTasksByEndDate).forEach((task) => {
      const suggestion = orgDefaultWaterRate ?? previous;
      if (suggestion !== null) suggestions.set(task.taskId, suggestion);
      if (isApplicationLikeTask(task) && task.waterRate !== null) {
        previous = task.waterRate;
      }
    });
    return suggestions;
  }, [showSheet, draftTasks, orgDefaultWaterRate]);

  const handleStepInputChange = useCallback(
    (task: IInputTask, stepId: string, selections: StepSelection[]) => {
      if (selections.length === 0) return;

      if (!isStepAllowedForTask(selections[0].stepType, task.taskType)) {
        toast.error(TASKS_GRID_STEP_TYPE_NOT_ALLOWED_MESSAGE);
        return;
      }

      // Defence-in-depth: the picker already locks the category tabs, but if
      // somehow a type change reaches here for a Seed step that a SeedDress
      // links to, reject it — replacing the step type would turn the link into
      // a dangling reference with no recoverable path.
      const existingStep = getTaskSteps(task).find((s) => s.stepId === stepId);
      if (
        existingStep?.stepType === InputStepType.Seed &&
        selections[0].stepType !== InputStepType.Seed
      ) {
        const linkedItems = getLinkedSeedDressItems(task, stepId);
        if (linkedItems.length > 0) {
          toast.error(
            buildSeedLinkedReason(linkedItems, ' to change step type')
          );
          return;
        }
      }

      const builtStep: IInputStep = {
        ...buildStepFromSelection(
          task,
          selections[0],
          userProfile,
          productionCropContext
        ),
        stepId,
      };
      const rebuiltStep = existingStep
        ? keepProductionStepValues(existingStep, builtStep)
        : builtStep;

      const updatedTask: IInputTask = syncYieldLinkedSteps({
        ...task,
        steps: task.steps.map((step) =>
          step.stepId === stepId ? rebuiltStep : step
        ),
      });

      updateDraftTask(updatedTask);
      schedulePendingTaskSave(updatedTask);
      setEditingStepInputId(null);
    },
    [
      updateDraftTask,
      schedulePendingTaskSave,
      userProfile,
      productionCropContext,
    ]
  );

  // The row's own "+ Add" for the filtered category (AC 16): on any collapsed
  // task the category can go in, matching or not, so a product can be added
  // without expanding. The task is highlighted where it lands, since gaining
  // a match moves it up the list.
  const renderRowAddInput = (task: IInputTask, isExpanded: boolean) => {
    if (!isFiltered || !activeFilter?.groupKey || isExpanded) return null;
    // A Production task takes its production step, then yield-linked
    // Operation costs (OP-2399).
    const canAdd = (activeFilter.stepTypes ?? []).some((stepType) =>
      canAddStepTypeToTask(stepType, task, isStepAllowedForTask)
    );
    if (!canAdd) return null;
    const needsProductionStep =
      task.taskType === InputTaskType.Production && !getProductionStep(task);
    return (
      <AddInputControl
        task={task}
        label={getAddCategoryLabel(activeFilter.label)}
        addInputCategoryKey={activeFilter.groupKey}
        enableMultiSelect
        disabledReason={
          needsProductionStep && productionCropContext.isResolving
            ? PRODUCTION_CROP_RESOLVING_TOOLTIP
            : undefined
        }
        isOpen={activeSelectorTaskId === task.taskId}
        onOpenChange={(open) => {
          closeContextMenu();
          setActiveSelectorTaskId(open ? task.taskId : null);
        }}
        onClose={closeInputSelector}
        onSelect={(selections) => {
          handleStepSelect(task, selections);
          setJustFinishedTaskId(task.taskId);
          setPendingFocus({ taskId: task.taskId, target: 'row' });
        }}
      />
    );
  };

  // A task's "+ Add" for the filtered category in the category sheet: on the
  // Task cell, or across the row of a task with none of the category yet.
  // The task is highlighted where it lands, since gaining its first product
  // moves it up into the plan.
  const renderAddControl = (task: IInputTask, categoryLabel?: string) => {
    const isProductionTask = task.taskType === InputTaskType.Production;
    // A Production task takes its production step first, then yield-linked
    // Operation costs (OP-2399).
    const needsProductionStep = isProductionTask && !getProductionStep(task);
    // All opens the picker on the task's own first category (Production for
    // a Production task); a filter opens it on the filter's category.
    const canAdd =
      isFiltered &&
      !!activeFilter?.groupKey &&
      (activeFilter.stepTypes ?? []).some((stepType) =>
        canAddStepTypeToTask(stepType, task, isStepAllowedForTask)
      );
    // Under a filter a Production task has nothing it can take, e.g. a second
    // production step under Production.
    if (isProductionTask && isFiltered && !canAdd) return null;
    return (
      <AddInputControl
        task={task}
        hasFilledIcon
        label={
          categoryLabel && canAdd
            ? getAddCategoryLabel(categoryLabel)
            : TASKS_SHEET_ADD_INPUT_LABEL
        }
        addInputCategoryKey={
          canAdd
            ? activeFilter?.groupKey
            : isProductionTask
              ? needsProductionStep
                ? PRODUCTION_STEP_GROUP_KEY
                : OPERATION_STEP_GROUP_KEY
              : undefined
        }
        enableMultiSelect
        disabledReason={
          needsProductionStep && productionCropContext.isResolving
            ? PRODUCTION_CROP_RESOLVING_TOOLTIP
            : undefined
        }
        isOpen={activeSelectorTaskId === task.taskId}
        onOpenChange={(open) => {
          closeContextMenu();
          setOpenDetailsTaskId(null);
          setActiveSelectorTaskId(open ? task.taskId : null);
        }}
        onClose={closeInputSelector}
        onSelect={(selections) => {
          const isFirstMatch =
            isFiltered && !matchingTaskIds.has(task.taskId);
          handleStepSelect(task, selections);
          if (isFirstMatch && task.taskId !== entryTaskId) {
            setJustFinishedTaskId(task.taskId);
            setPendingFocus({ taskId: task.taskId, target: 'row' });
          }
        }}
      />
    );
  };

  const renderTaskSummaryRow = (task: IInputTask, index: number) => (
    <TaskSummaryRow
      task={task}
      index={index}
      isExpanded={
        expandedTaskKeys.includes(task.taskId) && !isCompactTask(task.taskId)
      }
      isMenuOpen={
        contextMenuState?.target.rowType === 'task' &&
        contextMenuState.target.taskId === task.taskId
      }
      gUnits={gUnits}
      selectedInputStartDate={selectedInput.startDate}
      activeStepCategoryKey={activeStepCategoryKey}
      areaUnitOptions={areaUnitOptions}
      isCompact={isCompactTask(task.taskId)}
      rowAddInput={renderRowAddInput(
        task,
        expandedTaskKeys.includes(task.taskId) && !isCompactTask(task.taskId)
      )}
      inlineDetails={
        expandedTaskKeys.includes(task.taskId) &&
        !isCompactTask(task.taskId) ? (
          <TaskInlineDetails
            task={task}
            gUnits={gUnits}
            fieldVisibilityOverride={fieldVisibilityOverride}
            setFieldVisible={setFieldVisible}
            commitTaskField={commitTaskField}
          />
        ) : null
      }
      commitTaskField={commitTaskField}
      onToggleExpand={toggleTaskExpand}
      onDateEditEnd={handleDateEditEnd}
      onOpenMenuFromKebab={openMenuFromKebab}
    />
  );

  const taskColumns: ColumnsType<IInputTask> = useMemo(
    () => [
      {
        // The column label doubles as the spot for a brief note about the
        // tasks below it — next to where the user was working, rather than a
        // toast in a corner.
        title: (
          <div
            className={MergeClassNames(
              'task-column-title',
              emptyTaskRemovedAt !== null && 'task-column-title-with-notice'
            )}
          >
            <div className="flex min-w-0 items-center gap-3">
              <span className="task-column-title-label flex-shrink-0">
                {TASKS_GRID_COLUMN_LABEL_TASK}
              </span>
              <span role="status" className="min-w-0 truncate">
                {emptyTaskRemovedAt !== null && (
                  <span className="task-column-notice font-normal text-charcoal">
                    {EMPTY_TASK_REMOVED_MESSAGE}
                  </span>
                )}
              </span>
            </div>
          </div>
        ),
        key: 'task',
        // No width: the Task column takes whatever the fixed columns leave.
        onHeaderCell: () => ({
          className: 'task-column-header-cell',
        }),
        onCell: () => ({ colSpan: TASK_ROW_COLUMN_COUNT }),
        render: (_value, task, index) => renderTaskSummaryRow(task, index),
      },
      {
        title: TASKS_GRID_COLUMN_LABEL_DATE,
        key: 'date',
        width: `${TASK_ROW_COLUMN_WIDTHS_REM.date}rem`,
        onCell: () => ({ colSpan: 0 }),
        render: () => null,
      },
      {
        title: TASKS_GRID_COLUMN_LABEL_TIMING,
        key: 'timing',
        width: `${TASK_ROW_COLUMN_WIDTHS_REM.timing}rem`,
        onCell: () => ({ colSpan: 0 }),
        render: () => null,
      },
      {
        title: TASKS_GRID_COLUMN_LABEL_AREA,
        dataIndex: 'area',
        key: 'area',
        width: `${TASK_ROW_COLUMN_WIDTHS_REM.area}rem`,
        onCell: () => ({ colSpan: 0 }),
        render: () => null,
      },
      {
        title: TASKS_GRID_COLUMN_LABEL_AREA_UNIT,
        dataIndex: ['areaUnit', 'unit'],
        key: 'areaUnit',
        width: `${TASK_ROW_COLUMN_WIDTHS_REM.areaUnit}rem`,
        onCell: () => ({ colSpan: 0 }),
        render: () => null,
      },
      {
        title: TASKS_GRID_COLUMN_LABEL_COST_PER_HA,
        key: 'cost',
        width: `${TASK_ROW_COLUMN_WIDTHS_REM.cost}rem`,
        align: 'right',
        onCell: () => ({ colSpan: 0 }),
        render: () => null,
      },
    ],
    [
      activeStepCategoryKey,
      emptyTaskRemovedAt,
      expandedTaskKeys,
      toggleTaskExpand,
      areaUnitOptions,
      gUnits,
      commitTaskField,
      // renderTaskSummaryRow reads contextMenuState to keep the row's
      // three-dot button visible while its own menu is open; without this the
      // memo would hand antd a render closure that never sees the menu open.
      contextMenuState,
      openMenuFromKebab,
      handleDateEditEnd,
      // renderTaskSummaryRow reads the filter's matches and the open picker
      // (for the row's own "+ Add").
      isCompactTask,
      activeSelectorTaskId,
      handleStepSelect,
      closeInputSelector,
      productionCropContext.isResolving,
      // The expanded row's own Water rate / Target / Notes controls.
      fieldVisibilityOverride,
      setFieldVisible,
    ]
  );

  const addInputCategoryKey =
    ADD_INPUT_CATEGORY_KEY_BY_FILTER[activeStepCategoryKey];

  const renderTaskDetailsSection = useCallback(
    (task: IInputTask) => (
      <TaskDetailsSection
        task={task}
        gUnits={gUnits}
        fieldVisibilityOverride={fieldVisibilityOverride}
        commitTaskField={commitTaskField}
      />
    ),
    [gUnits, fieldVisibilityOverride, commitTaskField]
  );

  const renderInputTableFooter = useCallback(
    (task: IInputTask, hiddenCount: number) => {
      const isProductionTask = task.taskType === InputTaskType.Production;
      // A Production task's production step comes first, then its
      // yield-linked Operation costs (OP-2399).
      const needsProductionStep = isProductionTask && !getProductionStep(task);
      return (
        <>
          <TaskFilterTally
            task={task}
            planInput={planInput}
            gUnits={gUnits}
            activeStepCategoryKey={activeStepCategoryKey}
          />
          <InputTableFooter
            task={task}
            hiddenCount={hiddenCount}
            addInputCategoryKey={
              isProductionTask
                ? needsProductionStep
                  ? PRODUCTION_STEP_GROUP_KEY
                  : OPERATION_STEP_GROUP_KEY
                : addInputCategoryKey
            }
            enableMultiSelect
            addInputDisabledReason={
              needsProductionStep && productionCropContext.isResolving
                ? PRODUCTION_CROP_RESOLVING_TOOLTIP
                : undefined
            }
            isAddInputOpen={activeSelectorTaskId === task.taskId}
            onAddInputOpenChange={(open) => {
              closeContextMenu();
              setActiveSelectorTaskId(open ? task.taskId : null);
            }}
            onAddInputClose={closeInputSelector}
            onAddInputSelect={(selections) => handleStepSelect(task, selections)}
          />
        </>
      );
    },
    [
      planInput,
      gUnits,
      activeStepCategoryKey,
      addInputCategoryKey,
      productionCropContext.isResolving,
      activeSelectorTaskId,
      closeContextMenu,
      closeInputSelector,
      handleStepSelect,
    ]
  );

  const emptyTasksPlaceholder = (
    <div className="flex min-h-[13.75rem] items-center justify-center px-4 py-4">
      <div className="mx-auto flex w-full max-w-md flex-col items-center rounded-md bg-gray-smoke px-6 py-5 text-center shadow-sm">
        <p className="text-base font-medium text-gray-dark">
          {TASKS_GRID_EMPTY_STATE_TITLE}
        </p>
        <p className="mt-1 text-sm text-gray-dark">
          {TASKS_GRID_EMPTY_STATE_HINT_PREFIX}{' '}
          <span className="font-semibold text-secondary-dark">
            {TASKS_GRID_EMPTY_STATE_HINT_BUTTON_LABEL}
          </span>{' '}
          {TASKS_GRID_EMPTY_STATE_HINT_SUFFIX}
        </p>
      </div>
    </div>
  );

  return (
    <div className="z-10 mt-3 min-h-0 min-w-0 bg-gray-bright">
      {contextMenuState && (
        <TaskRowContextMenu
          ref={contextMenuRef}
          x={contextMenuState.x}
          y={contextMenuState.y}
          alignRight={contextMenuState.alignRight}
          showViewDetails={showViewDetails}
          showReorder={contextMenuCanReorder}
          showChangeSeedTarget={stepContextMenu.showChangeSeedTarget}
          deleteDisabledReason={deleteDisabledReason}
          onViewDetails={handleViewDetailsFromContext}
          onReorder={handleReorderFromContext}
          onChangeSeedTarget={stepContextMenu.handleChangeSeedTargetFromContext}
          showUpdateDefaults={stepContextMenu.showUpdateDefaults}
          onUpdateDefaults={stepContextMenu.handleUpdateDefaultsFromContext}
          onDuplicate={
            contextMenuState.target.rowType === 'task'
              ? handleDuplicateFromContext
              : undefined
          }
          onDelete={handleDeleteFromContext}
        />
      )}

      {viewDetailsStep && (
        <InputStepDetailsModal
          step={viewDetailsStep}
          onClose={closeViewDetailsStep}
        />
      )}

      <QuickAddTaskRow onAdd={handleAddTask} trailing={quickAddTrailing} />

      {showSheet && activeFilter ? (
        <TaskCategorySheet
          activeFilter={activeFilter}
          planTasks={planTasks}
          missingTasks={missingTasks}
          planInput={planInput}
          gUnits={gUnits}
          areaUnitOptions={areaUnitOptions}
          preferences={viewPreferences}
          collapsedTaskIds={collapsedSheetTaskIds}
          onToggleTaskCollapsed={(taskId) =>
            onToggleSheetTaskCollapsed?.(taskId)
          }
          onTaskColumnWidthChange={(width) => onTaskColumnWidthChange?.(width)}
          emptyState={emptyTasksPlaceholder}
          isActual={activeTaskCategory === InputTaskCategory.Actual}
          orgDefaultWaterRate={orgDefaultWaterRate}
          waterRateSuggestions={waterRateSuggestions}
          entryTaskId={entryTaskId}
          justFinishedTaskId={justFinishedTaskId}
          openDetailsTaskId={openDetailsTaskId}
          setOpenDetailsTaskId={setOpenDetailsTaskId}
          contextMenuTarget={contextMenuState?.target ?? null}
          activeSelectorTaskId={activeSelectorTaskId}
          onMaxWidthClassChange={onSheetMaxWidthClassChange}
          onShowProductionFilter={onShowProductionFilter}
          onEntryAddInputs={handleEntryAddInputs}
          availableWidth={sheetAvailableWidth}
          renderAddControl={renderAddControl}
          commitTaskField={commitTaskField}
          commitStepField={commitStepField}
          onDateEditEnd={handleDateEditEnd}
          onTaskContextMenu={handleTaskRowContextMenu}
          onOpenMenuFromKebab={openMenuFromKebab}
          editingStepInputId={editingStepInputId}
          setEditingStepInputId={setEditingStepInputId}
          handleStepInputChange={handleStepInputChange}
          stepReorder={stepReorder}
          stepContextMenu={stepContextMenu}
        />
      ) : (
        <ConfigProvider
          theme={{
            components: {
              Table: {
                cellPaddingBlockSM: 0,
                cellPaddingInlineSM: 0,
                headerSplitColor: 'transparent',
              },
            },
          }}
        >
          <Table
            className="input-tasks-table"
            bordered
            size="small"
            pagination={false}
            tableLayout="fixed"
            rowKey={(task) => task.taskId}
            rowClassName={(task) =>
              MergeClassNames(
                // Drives the row's three-dot button reveal on hover.
                'group',
                expandedTaskKeys.includes(task.taskId) && 'font-semibold',
                expandedTaskKeys.includes(task.taskId) && 'expanded-task-row',
                contextMenuState?.target.rowType === 'task' &&
                  contextMenuState.target.taskId === task.taskId &&
                  'highlighted-row',
                task.taskId === entryTaskId && 'task-entry-row',
                task.taskId === justFinishedTaskId && 'task-row-just-finished',
                matchingTaskIds.has(task.taskId) && 'task-row-filter-match'
              )
            }
            onRow={(task) => ({
              onClick: () => {
                closeContextMenu();
                // A compact row has nothing to expand into under the filter.
                if (isCompactTask(task.taskId)) return;
                toggleTaskExpand(task.taskId);
              },
              onContextMenu: (event) => {
                handleTaskRowContextMenu(event, task.taskId);
              },
            })}
            columns={taskColumns}
            dataSource={tasks}
            locale={{ emptyText: emptyTasksPlaceholder }}
            expandable={{
              rowExpandable: () => true,
              showExpandColumn: false,
              indentSize: 0,
              // Compact rows stay collapsed while the filter is on; their
              // expanded state is kept for when it's cleared.
              expandedRowKeys: expandedTaskKeys.filter(
                (taskId) => !isCompactTask(taskId)
              ),
              expandedRowClassName: (task) =>
                task.taskId === entryTaskId ? 'task-entry-expanded-row' : '',
              onExpandedRowsChange: (expandedRows) => {
                setExpandedTaskKeys((previous) => [
                  ...previous.filter((taskId) => isCompactTask(taskId)),
                  ...(expandedRows as string[]),
                ]);
              },
              expandedRowRender: (task) =>
                task.taskType === InputTaskType.Production ? (
                  <ProductionTaskSteps
                    task={task}
                    editingStepInputId={editingStepInputId}
                    setEditingStepInputId={setEditingStepInputId}
                    handleStepInputChange={handleStepInputChange}
                    commitStepField={commitStepField}
                    stepContextMenu={stepContextMenu}
                    renderTaskDetailsSection={renderTaskDetailsSection}
                    renderInputTableFooter={renderInputTableFooter}
                    renderYieldLinkedSteps={(productionTask) => (
                      // The details card is above the matrix already.
                      <TaskStepsTable
                        task={productionTask}
                        stepTypes={PRODUCTION_COST_STEP_TYPES}
                        activeStepCategoryKey={activeStepCategoryKey}
                        gUnits={gUnits}
                        editingStepInputId={editingStepInputId}
                        setEditingStepInputId={setEditingStepInputId}
                        handleStepInputChange={handleStepInputChange}
                        commitStepField={commitStepField}
                        stepReorder={stepReorder}
                        stepContextMenu={stepContextMenu}
                        renderTaskDetailsSection={() => null}
                        renderInputTableFooter={renderInputTableFooter}
                      />
                    )}
                  />
                ) : (
                  <TaskStepsTable
                    task={task}
                    activeStepCategoryKey={activeStepCategoryKey}
                    gUnits={gUnits}
                    editingStepInputId={editingStepInputId}
                    setEditingStepInputId={setEditingStepInputId}
                    handleStepInputChange={handleStepInputChange}
                    commitStepField={commitStepField}
                    stepReorder={stepReorder}
                    stepContextMenu={stepContextMenu}
                    renderTaskDetailsSection={renderTaskDetailsSection}
                    renderInputTableFooter={renderInputTableFooter}
                  />
                ),
            }}
          />
        </ConfigProvider>
      )}
    </div>
  );
};

export default TasksTableGrid;
